import * as React from "react"
import { Button } from "./ui"
import { supabase } from "./supabase"
import {
  addCalendarDays,
  addCalendarMonths,
  calendarMonths,
  civilDate,
  dateAvailability,
  dateLabel,
  londonToday,
  millisecondsUntilNextLondonDay,
  type DateAvailabilityOverride,
  type WeeklyAvailabilityRule,
} from "./dateSettingsCalendar"

type ShortNotice = { sameDay: boolean; nextDay: boolean }
type CalendarData = { from: string; to: string; available: Record<string, boolean>; shortNotice: Record<string, ShortNotice> }
type Feedback = { tone: "error" | "success"; text: string } | null

function errorMessage(value: unknown): string {
  if (value && typeof value === "object" && "message" in value && typeof value.message === "string") return value.message
  return "Please check your connection and try again."
}

const productTypes = [
  { value: "jetwash", label: "Jet Washers", image: "jet-washer" },
  { value: "dehumidifier", label: "Dehumidifiers", image: "dehumidifier" },
  { value: "portable-ac", label: "Portable ACs", image: "portable-ac" },
] as const
type ProductGroup = typeof productTypes[number]["value"]

export function DateSettings({ active, canWrite }: { active: boolean; canWrite: boolean }) {
  const [productGroup, setProductGroup] = React.useState<ProductGroup>("jetwash")
  const tabs = React.useRef(new Map<ProductGroup, HTMLButtonElement>())
  return <>
    <div className="fleet-equipment date-product-tabs" role="tablist" aria-label="Date availability by product type">
      {productTypes.map((product, index) => <button key={product.value} type="button" role="tab"
        aria-label={product.label} title={product.label}
        id={`date-tab-${product.value}`} aria-controls={`date-panel-${product.value}`}
        aria-selected={productGroup === product.value} tabIndex={productGroup === product.value ? 0 : -1}
        ref={(button) => { if (button) tabs.current.set(product.value, button); else tabs.current.delete(product.value) }}
        onClick={() => setProductGroup(product.value)}
        onKeyDown={(event) => {
          const next = event.key === "ArrowRight" ? (index + 1) % productTypes.length
            : event.key === "ArrowLeft" ? (index + productTypes.length - 1) % productTypes.length
            : event.key === "Home" ? 0 : event.key === "End" ? productTypes.length - 1 : null
          if (next === null) return
          event.preventDefault()
          setProductGroup(productTypes[next].value)
          tabs.current.get(productTypes[next].value)?.focus()
        }}
      ><img src={`/equipment-icons/${product.image}.png`} alt="" aria-hidden="true" width="52" height="58" /></button>)}
    </div>
    {productTypes.map((product) => <div key={product.value} role="tabpanel"
      id={`date-panel-${product.value}`} aria-labelledby={`date-tab-${product.value}`} hidden={productGroup !== product.value}>
      <ProductDateSettings active={active && productGroup === product.value} canWrite={canWrite} productGroup={product.value} />
    </div>)}
  </>
}

function ProductDateSettings({ active, canWrite, productGroup }: { active: boolean; canWrite: boolean; productGroup: ProductGroup }) {
  const productLabel = productTypes.find((product) => product.value === productGroup)!.label
  const [data, setData] = React.useState<CalendarData | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState("")
  const [pending, setPending] = React.useState<Record<string, boolean>>({})
  const [feedback, setFeedback] = React.useState<Feedback>(null)
  const pendingDates = React.useRef(new Set<string>())
  const loadId = React.useRef(0)
  const refreshAfterSave = React.useRef(false)
  const mounted = React.useRef(true)
  const buttons = React.useRef(new Map<string, HTMLButtonElement>())

  const reload = React.useCallback(async () => {
    if (pendingDates.current.size) {
      refreshAfterSave.current = true
      return
    }
    const requestId = ++loadId.current
    const from = londonToday()
    const to = addCalendarMonths(from, 3)
    setLoading(true)
    setLoadError("")
    try {
      const [weekly, overrides] = await Promise.all([
        supabase.from("product_service_weekly_rules").select("weekday,delivery_enabled,collection_enabled").eq("product_group", productGroup),
        supabase.from("product_service_date_overrides").select("service_date,delivery_enabled,collection_enabled,same_day_enabled,next_day_enabled").eq("product_group", productGroup).gte("service_date", from).lte("service_date", to),
      ])
      if (weekly.error) throw weekly.error
      if (overrides.error) throw overrides.error
      if (!mounted.current || requestId !== loadId.current) return
      const dates = (overrides.data ?? []) as Array<DateAvailabilityOverride & { same_day_enabled: boolean; next_day_enabled: boolean }>
      setData({ from, to, available: dateAvailability(from, to, (weekly.data ?? []) as WeeklyAvailabilityRule[], dates),
        shortNotice: Object.fromEntries(dates.map((date) => [date.service_date, { sameDay: Boolean(date.same_day_enabled), nextDay: Boolean(date.next_day_enabled) }])) })
    } catch (error) {
      if (mounted.current && requestId === loadId.current) setLoadError(errorMessage(error))
    } finally {
      if (mounted.current && requestId === loadId.current) setLoading(false)
    }
  }, [productGroup])

  React.useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false; loadId.current += 1 }
  }, [])

  React.useEffect(() => {
    if (!active) return
    void reload()
    const refresh = () => { if (document.visibilityState !== "hidden") void reload() }
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", refresh)
    let boundaryTimer: ReturnType<typeof setTimeout>
    const scheduleBoundary = () => {
      boundaryTimer = setTimeout(() => { void reload(); scheduleBoundary() }, millisecondsUntilNextLondonDay())
    }
    scheduleBoundary()
    return () => {
      window.removeEventListener("focus", refresh)
      document.removeEventListener("visibilitychange", refresh)
      clearTimeout(boundaryTimer)
    }
  }, [active, reload])

  const toggle = async (day: string, field: "available" | keyof ShortNotice = "available") => {
    if (!canWrite || !data || loading || loadError || pendingDates.current.has(day)) return
    if (field !== "available" && !data.available[day]) return
    const previous = data.available[day]
    const previousNotice = data.shortNotice[day] ?? { sameDay: false, nextDay: false }
    const available = field === "available" ? !previous : previous
    const notice = field === "available" ? previousNotice : { ...previousNotice, [field]: !previousNotice[field] }
    pendingDates.current.add(day)
    setPending((current) => ({ ...current, [day]: true }))
    setData((current) => current && ({ ...current, available: { ...current.available, [day]: available }, shortNotice: { ...current.shortNotice, [day]: notice } }))
    setFeedback(null)
    try {
      const { error } = await supabase.rpc("admin_set_date_availability", {
        p_product_group: productGroup, p_service_date: day, p_available: field === "available" ? available : null,
        ...(field === "available" ? {} : { p_same_day_enabled: field === "sameDay" ? notice.sameDay : null, p_next_day_enabled: field === "nextDay" ? notice.nextDay : null }),
      })
      if (error) throw error
      if (mounted.current) setFeedback({ tone: "success", text: field === "available"
        ? `${productLabel}: ${dateLabel(day)} is ${available ? "available" : "blocked"} for delivery and collection.`
        : `${productLabel}: ${field === "sameDay" ? "Same-day" : "Extended next-day"} booking ${notice[field] ? "enabled" : "disabled"} for ${dateLabel(day)}. Local postcodes only.` })
    } catch (error) {
      if (mounted.current) {
        setData((current) => current && ({ ...current, available: { ...current.available, [day]: previous }, shortNotice: { ...current.shortNotice, [day]: previousNotice } }))
        refreshAfterSave.current = true
        setFeedback({ tone: "error", text: `The change to ${dateLabel(day)} could not be confirmed. ${errorMessage(error)}` })
      }
    } finally {
      pendingDates.current.delete(day)
      if (mounted.current) {
        setPending((current) => { const next = { ...current }; delete next[day]; return next })
        if (!pendingDates.current.size && refreshAfterSave.current) {
          refreshAfterSave.current = false
          void reload()
        }
      }
    }
  }

  const navigateDates = (event: React.KeyboardEvent<HTMLButtonElement>, day: string) => {
    let target: string
    switch (event.key) {
      case "ArrowLeft": target = addCalendarDays(day, -1); break
      case "ArrowRight": target = addCalendarDays(day, 1); break
      case "ArrowUp": target = addCalendarDays(day, -7); break
      case "ArrowDown": target = addCalendarDays(day, 7); break
      case "Home": target = addCalendarDays(day, -((civilDate(day).getUTCDay() + 6) % 7)); break
      case "End": target = addCalendarDays(day, 6 - ((civilDate(day).getUTCDay() + 6) % 7)); break
      default: return
    }
    event.preventDefault()
    if (data) buttons.current.get(target < data.from ? data.from : target > data.to ? data.to : target)?.focus()
  }

  return <section className="panel date-settings" aria-label={`${productLabel} delivery and collection dates`}>
    {feedback && <div className={`message date-settings-feedback ${feedback.tone}`} role={feedback.tone === "error" ? "alert" : "status"}><span>{feedback.text}</span><Button type="button" aria-label="Dismiss date settings message" onClick={() => setFeedback(null)}>×</Button></div>}
    {loading ? <div className="section-loading" role="status"><span aria-hidden="true" /> Loading date settings…</div> : loadError ? <div className="date-settings-error" role="alert"><h3>Date settings could not load</h3><p>{loadError}</p><Button type="button" className="secondary" onClick={() => void reload()}>Retry</Button></div> : data && <>
      <div className="date-months">{calendarMonths(data.from, data.to).map((month) => <section className="date-month" key={month.key} aria-labelledby={`month-${productGroup}-${month.key}`}>
        <h3 id={`month-${productGroup}-${month.key}`}>{month.label}</h3>
        <div className="date-weekdays" aria-hidden="true">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => <span key={day}>{day}</span>)}</div>
        <div className="date-calendar">{month.days.map((day, index) => day ? <div className="date-cell" key={day}><Button
          type="button"
          ref={(button) => { if (button) buttons.current.set(day, button); else buttons.current.delete(day) }}
          className={`date-day ${data.available[day] ? "date-available" : "date-blocked"}${day === data.from ? " date-today" : ""}`}
          data-date={day}
          aria-label={`${dateLabel(day)}: delivery and collection ${data.available[day] ? "available" : "blocked"}${pending[day] ? ", saving" : ""}`}
          aria-pressed={data.available[day]}
          aria-current={day === data.from ? "date" : undefined}
          aria-busy={Boolean(pending[day])}
          disabled={!canWrite || Boolean(pending[day])}
          title={canWrite ? `${data.available[day] ? "Block" : "Enable"} delivery and collection` : "View only"}
          onClick={() => void toggle(day)}
          onKeyDown={(event) => navigateDates(event, day)}
        ><span className="date-day-content"><span className="date-day-number">{Number(day.slice(-2))}</span><span className={pending[day] ? "date-saving" : "date-day-mark"} aria-hidden="true">{pending[day] ? "" : data.available[day] ? "✓" : "×"}</span></span></Button>
          {(["sameDay", "nextDay"] as const).map((field) => <Button key={field} type="button"
            className="date-notice-toggle" data-notice-date={day} data-notice={field}
            aria-label={`${dateLabel(day)}: ${field === "sameDay" ? "same-day booking until 6pm" : "next-day booking after noon"}, local postcodes only`}
            aria-pressed={Boolean(data.shortNotice[day]?.[field])} aria-busy={Boolean(pending[day])}
            disabled={!canWrite || !data.available[day] || Boolean(pending[day])}
            onClick={() => void toggle(day, field)}
          ><span className="date-notice-content"><span aria-hidden="true">{data.shortNotice[day]?.[field] ? "✓" : "○"}</span><span>{field === "sameDay" ? "Same day" : "Next day"}</span></span></Button>)}
        </div> : <span key={`empty-${index}`} aria-hidden="true" />)}</div>
      </section>)}</div>
    </>}
  </section>
}
